import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useLocation } from 'wouter';

interface LoginForm {
  username: string;
  password: string;
}

export default function Login() {
  const [formData, setFormData] = useState<LoginForm>({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const loginMutation = useMutation({
    mutationFn: async (data: LoginForm) => {
      const response = await apiRequest('POST', '/api/login', data);
      return await response.json();
    },
    onSuccess: (data: any) => {
      queryClient.setQueryData(['/api/auth/user'], data.user);
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      toast({
        title: 'Success',
        description: 'Logged in successfully',
      });
      // Small delay to ensure auth state updates
      setTimeout(() => {
        setLocation('/');
      }, 100);
    },
    onError: (error: any) => {
      toast({
        title: 'Login Failed',
        description: error.message || 'Invalid username or password',
        variant: 'destructive',
      });
    },
  });

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    loginMutation.mutate(formData);
  };

  return (
    <div style={{ fontFamily: "'Nunito', sans-serif", background: '#000', minHeight: '100dvh' }}>
      {/* Page Wrapper with Background Image */}
      <div
        style={{
          minHeight: '100dvh',
          backgroundImage: 'url(/surya-bg-img.png)',
          backgroundSize: 'auto 100%',
          backgroundPosition: 'left center',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          padding: '0 5vw',
        }}
      >
        {/* Login Container */}
        <div style={{ width: '100%', maxWidth: '460px' }}>
          {/* Login Card */}
          <div
            style={{
              background: '#0d5366',
              padding: '48px 40px',
              color: 'white',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.35)',
            }}
          >
            {/* Header Section */}
            <div style={{ textAlign: 'center', marginBottom: '44px' }}>
              <img
                src="/logo.png"
                alt="SuryaCon Logo"
                style={{
                  height: '88px',
                  marginBottom: '20px',
                  display: 'block',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
                onError={(e) => {
                  // Fallback to logomanagement.png if logo.png doesn't exist
                  (e.target as HTMLImageElement).src = '/logomanagement.png';
                }}
              />
              <h1 style={{
                fontSize: '30px',
                fontWeight: 800,
                letterSpacing: '0.3px',
                lineHeight: 1.1,
              }}>
                Task Management
              </h1>
            </div>

            {/* Form */}
            <form onSubmit={handleLogin}>
              {/* Username Field */}
              <div style={{ position: 'relative', marginBottom: '28px' }}>
                <Label
                  htmlFor="username"
                  style={{
                    display: 'block',
                    fontSize: '15px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    color: '#e0f7ff',
                  }}
                >
                  Username
                </Label>
                <Input
                  id="username"
                  type="text"
                  required
                  value={formData.username}
                  onChange={e =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  style={{
                    width: '100%',
                    height: '56px',
                    padding: '0 18px',
                    fontSize: '16px',
                    fontFamily: 'inherit',
                    color: '#0d5366',
                    background: '#ffffff',
                    border: '1px solid #d0e8f5',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                  }}
                  className="login-input-username"
                  placeholder="username"
                />
              </div>

              {/* Password Field */}
              <div style={{ position: 'relative', marginBottom: '28px' }}>
                <Label
                  htmlFor="password"
                  style={{
                    display: 'block',
                    fontSize: '15px',
                    fontWeight: 600,
                    marginBottom: '8px',
                    color: '#e0f7ff',
                  }}
                >
                  Password
                </Label>
                <div style={{ position: 'relative' }}>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={e =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    style={{
                      width: '100%',
                      height: '56px',
                      padding: '0 48px 0 18px',
                      fontSize: '16px',
                      fontFamily: 'inherit',
                      color: '#0d5366',
                      background: '#ffffff',
                      border: '1px solid #d0e8f5',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                    }}
                    className="login-input-password"
                    placeholder="••••••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#555',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? (
                      <EyeOff size={20} style={{ color: '#555' }} />
                    ) : (
                      <Eye size={20} style={{ color: '#555' }} />
                    )}
                  </button>
                </div>
              </div>

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={loginMutation.isPending}
                style={{
                  width: '100%',
                  height: '56px',
                  marginTop: '12px',
                  background: '#ffc300',
                  color: '#0d1f2d',
                  fontSize: '18px',
                  fontWeight: 800,
                  letterSpacing: '0.4px',
                  border: 'none',
                  cursor: loginMutation.isPending ? 'not-allowed' : 'pointer',
                  boxShadow: '0 6px 24px rgba(255, 195, 0, 0.4)',
                  transition: 'all 0.18s ease',
                  opacity: loginMutation.isPending ? 0.7 : 1,
                }}
                className="btn-signin"
                onMouseEnter={(e) => {
                  if (!loginMutation.isPending) {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 10px 32px rgba(255, 195, 0, 0.55)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loginMutation.isPending) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 6px 24px rgba(255, 195, 0, 0.4)';
                  }
                }}
              >
                {loginMutation.isPending ? 'Signing in...' : 'Sign In'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* CSS Styles */}
      <style>{`
        .login-input-username::placeholder,
        .login-input-password::placeholder {
          color: #a0c0d0;
          opacity: 1;
        }

        .login-input-username:focus,
        .login-input-password:focus {
          outline: none;
          border-color: #0d5366 !important;
          box-shadow: 0 0 0 3px rgba(13, 83, 102, 0.15) !important;
        }

        .btn-signin:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .btn-signin:disabled:hover {
          transform: translateY(0) !important;
        }

        @media (max-width: 840px) {
          div[style*="justify-content: flex-end"] {
            justify-content: center !important;
            padding: 0 24px !important;
            background-position: left center !important;
            background-size: auto 100% !important;
          }

          div[style*="background: #0d5366"] {
            padding: 40px 28px !important;
          }
        }

        @media (max-width: 480px) {
          img[alt="SuryaCon Logo"] {
            height: 72px !important;
          }

          h1 {
            font-size: 26px !important;
          }
        }
      `}</style>
    </div>
  );
}
